// src/Invoice-Retrieval/InvoiceRetrieval.jsx

import React, { useState } from "react";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import api from "../axiosConfig";
import {
  Box,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper
} from "@mui/material";
import { getUser } from "../helpers";

export default function InvoiceRetrieval() {
const [loading, setLoading] = useState(false);
const [results, setResults] = useState({
    success: [],
    notFound: [],
});

  const user = getUser(); 
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setResults({ success: [], notFound: [] });

    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

    // remove header
    const records = rows.slice(1)
    .filter(row => row && row.length >= 3)
    .map((row) => ({
      invoiceNo: row[0],
      outletCode: row[1],
      outletId: row[2],
    }));

    console.log("Parsed records:", records);

    await api.post("/invoice-retrieval-log", {
    user,
    event: "fileUploaded",
    payload: {
        totalRecords: records.length
    }
    });

    const zip = new JSZip();
    const success = [];
    const notFound = [];

    for (const record of records) {
    console.log("Processing:", record);
    if (!record.invoiceNo || !record.outletId) {
        if (record.invoiceNo && !notFound.includes(record.invoiceNo)) {
        notFound.push(record.invoiceNo);
        }
        continue;
    }

    try {
        console.log("Calling API:", {
            invoiceNo: record.invoiceNo,
            outletId: record.outletId,
            user: user,
            });
        const res = await api.get("/get-invoice-pdf", {
        params: {
            invoiceNo: record.invoiceNo,
            outletId: record.outletId,
            user: user, 
        },
        responseType: "blob",
        });

        console.log("Response received:", {
            invoice: record.invoiceNo,
            status: res.status,
            size: res.data?.size,
            type: res.headers["content-type"],
            });

        if (res.data && res.data.size > 0) {
        zip.file(`${record.invoiceNo}.pdf`, res.data);

        if (!success.includes(record.invoiceNo)) {
            success.push(record.invoiceNo);
        }
        } else {
        if (!notFound.includes(record.invoiceNo)) {
            notFound.push(record.invoiceNo);
        }
        }
    } catch (err) {
        console.error("API ERROR:", {
            invoice: record.invoiceNo,
            status: err.response?.status,
            data: err.response?.data,
            message: err.message,
        });

        await api.post("/invoice-retrieval-log", {
        user,
        event: "failedInvoiceRetrieval",
        payload: {
            invoiceNo: record.invoiceNo,
            outletId: record.outletId,
            error: err.message
        }
        });

        if (!notFound.includes(record.invoiceNo)) {
        notFound.push(record.invoiceNo);
        }
    }
    }

    await api.post("/invoice-retrieval-log", {
    user,
    event: "downloadSummary",
    payload: {
        successCount: success.length,
        failureCount: notFound.length,
        successInvoices: success,
        failedInvoices: notFound
    }
    });

    setResults({ success, notFound });

    if (success.length > 0) {
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, "Invoices.zip");
    }

    setLoading(false);
    };

    return (
    <Box p={3}>
        <Typography variant="h5" fontWeight={600} mb={2}>
        Invoice Retrieval
        </Typography>

        <input type="file" 
            accept=".xlsx" 
            onClick={() => {
                api.post("/invoice-retrieval-log", {
                user,
                event: "fileClick",
                payload: {}
                });
            }}
            onChange={handleFileUpload} 
        />

        {loading && (
        <Box mt={3}>
            <Typography variant="body2" mb={1}>
            Processing invoices...
            </Typography>
            <LinearProgress />
        </Box>
        )}

        {!loading && (
        <>
            {/* Summary Cards */}
            <Grid container spacing={2} mt={2}>
            <Grid item xs={6}>
                <Card sx={{ backgroundColor: "#e8f5e9" }}>
                <CardContent>
                    <Typography variant="subtitle2">
                    Downloaded
                    </Typography>
                    <Typography variant="h4">
                    {results.success.length}
                    </Typography>
                </CardContent>
                </Card>
            </Grid>

            <Grid item xs={6}>
                <Card sx={{ backgroundColor: "#ffebee" }}>
                <CardContent>
                    <Typography variant="subtitle2">
                    Failed / Not Found
                    </Typography>
                    <Typography variant="h4">
                    {results.notFound.length}
                    </Typography>
                </CardContent>
                </Card>
            </Grid>
            </Grid>

            {/* Success List */}
            {results.success.length > 0 && (
            <Box mt={4}>
                <Typography variant="h6" mb={1}>
                Downloaded Invoices
                </Typography>

                <Paper
                sx={{
                    p: 2,
                    maxHeight: 220,
                    overflowY: "auto",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                }}
                >
                {results.success.map((inv, idx) => (
                    <Chip
                    key={idx}
                    label={inv}
                    color="success"
                    variant="outlined"
                    />
                ))}
                </Paper>
            </Box>
            )}

            {/* Failed List */}
            {results.notFound.length > 0 && (
            <Box mt={4}>
                <Typography variant="h6" mb={1}>
                Failed / Not Found
                </Typography>

                <Paper
                sx={{
                    p: 2,
                    maxHeight: 220,
                    overflowY: "auto",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    backgroundColor: "#fff5f5",
                }}
                >
                {results.notFound.map((inv, idx) => (
                    <Chip
                    key={idx}
                    label={inv}
                    color="error"
                    variant="outlined"
                    />
                ))}
                </Paper>
            </Box>
            )}
        </>
        )}
    </Box>
    );
}